import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DestructiveConfirmDialog } from "@/components/ui/destructive-confirm-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, Save, Plus, Trash2, FileSpreadsheet, ChevronRight, Database } from "lucide-react";
import { toast } from "sonner";

export default function ExcelWorkspace() {
  const [workspaces, setWorkspaces] = useState([]);
  const [active, setActive] = useState(null); // {id, name, columns, rows}
  const [dirty, setDirty] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/excel/workspaces").then((r) => setWorkspaces(r.data));
  useEffect(() => { load(); }, []);

  const openWs = async (id) => {
    const r = await api.get(`/excel/workspaces/${id}`);
    setActive(r.data); setDirty(false);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name);
    try {
      const r = await api.post("/excel/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Đã import ${r.data.rows.length} dòng × ${r.data.columns.length} cột`);
      load();
      setActive({ id: r.data.id, name: r.data.name, columns: r.data.columns, rows: r.data.rows });
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import lỗi");
    }
  };

  const saveActive = async () => {
    if (!active) return;
    try {
      await api.put(`/excel/workspaces/${active.id}`, { columns: active.columns, rows: active.rows });
      toast.success("Đã lưu workspace"); setDirty(false); load();
    } catch { toast.error("Lưu thất bại"); }
  };

  const exportXlsx = () => {
    const token = localStorage.getItem("ts_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/excel/workspaces/${active.id}/export`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then((r) => r.blob()).then((b) => {
      const u = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = active.name.endsWith(".xlsx") ? active.name : active.name + ".xlsx";
      a.click(); URL.revokeObjectURL(u); toast.success("Đã xuất Excel");
    });
  };

  const deleteWs = async (id) => {
    await api.delete(`/excel/workspaces/${id}`);
    if (active?.id === id) setActive(null);
    load(); toast.success("Đã xóa");
  };

  const updateCell = (rowIdx, col, value) => {
    setActive((a) => {
      const rows = [...a.rows];
      rows[rowIdx] = { ...rows[rowIdx], [col]: value };
      return { ...a, rows };
    });
    setDirty(true);
  };
  const addRow = () => {
    setActive((a) => {
      const empty = Object.fromEntries(a.columns.map((c) => [c, ""]));
      return { ...a, rows: [...a.rows, empty] };
    });
    setDirty(true);
  };
  const deleteRow = (idx) => {
    setActive((a) => ({ ...a, rows: a.rows.filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  return (
    <div data-testid="page-excel">
      <PageHeader
        kicker="Công cụ"
        title="Excel Workspace"
        breadcrumb={["Công cụ", "Excel Workspace"]}
        actions={
          <>
            <input
              ref={fileRef} type="file" hidden accept=".xlsx,.xls"
              onChange={(e) => handleUpload(e.target.files?.[0])}
            />
            <Button onClick={() => fileRef.current?.click()} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-upload-excel">
              <Upload className="w-4 h-4 mr-1.5" />Upload Excel
            </Button>
            {active && (
              <>
                <Button onClick={saveActive} disabled={!dirty} variant="outline" className="rounded-sm" data-testid="btn-save-ws">
                  <Save className="w-4 h-4 mr-1.5" />Lưu{dirty && " *"}
                </Button>
                <Button onClick={exportXlsx} variant="outline" className="rounded-sm border-amber-600 text-amber-900" data-testid="btn-export-ws">
                  <Download className="w-4 h-4 mr-1.5" />Tải Excel
                </Button>
                <Button onClick={() => setApplyOpen(true)} variant="outline" className="rounded-sm" data-testid="btn-apply-master">
                  <Database className="w-4 h-4 mr-1.5" />Đẩy vào Master Data
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="flex h-[calc(100vh-145px)]">
        {/* Sidebar list */}
        <div className="w-64 bg-white border-r border-stone-200 overflow-y-auto flex-shrink-0">
          <div className="px-4 py-3 border-b border-stone-200">
            <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Workspaces</div>
            <div className="text-xs text-stone-400 mt-0.5">{workspaces.length} file đang lưu</div>
          </div>
          <ul>
            {workspaces.map((w) => (
              <li key={w.id} className="border-b border-stone-100">
                <button
                  onClick={() => openWs(w.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-stone-50 ${active?.id === w.id ? "bg-emerald-50 border-l-2 border-emerald-800" : ""}`}
                  data-testid={`ws-item-${w.id}`}
                >
                  <div className="flex items-start gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-emerald-800 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{w.name}</div>
                      <div className="text-[10px] text-stone-500 font-mono">{w.n_rows} dòng × {w.n_cols} cột</div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
            {workspaces.length === 0 && (
              <li className="px-4 py-8 text-center text-xs text-stone-400">
                Chưa có workspace.<br />Upload file Excel để bắt đầu.
              </li>
            )}
          </ul>
        </div>

        {/* Main grid area */}
        <div className="flex-1 flex flex-col overflow-hidden bg-stone-100">
          {active ? (
            <>
              <div className="bg-white border-b border-stone-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-4 h-4 text-emerald-800" />
                  <Input
                    value={active.name}
                    onChange={(e) => { setActive({ ...active, name: e.target.value }); setDirty(true); }}
                    className="border-0 font-semibold focus-visible:ring-1 focus-visible:ring-emerald-700 h-7 px-2 max-w-md"
                  />
                  <span className="text-xs text-stone-500 font-mono">{active.rows.length} × {active.columns.length}</span>
                </div>
                <div className="flex gap-1">
                  <Button onClick={addRow} size="sm" variant="ghost" className="text-emerald-800 h-7" data-testid="btn-add-row">
                    <Plus className="w-3.5 h-3.5 mr-1" />Thêm dòng
                  </Button>
                  <DestructiveConfirmDialog
                    title="Xóa workspace?"
                    description={`Workspace ${active.name || active.id} sẽ bị xóa.`}
                    confirmLabel="Xóa workspace"
                    token={String(active.name || active.id)}
                    onConfirm={() => deleteWs(active.id)}
                    trigger={(
                      <Button size="sm" variant="ghost" className="text-red-700 h-7">
                        <Trash2 className="w-3.5 h-3.5 mr-1" />Xóa
                      </Button>
                    )}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-auto bg-white" data-testid="excel-grid-container">
                <table className="excel-grid">
                  <thead>
                    <tr>
                      <th className="row-num">#</th>
                      {active.columns.map((c) => <th key={c}>{c}</th>)}
                      <th style={{ minWidth: "32px", maxWidth: "32px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.rows.map((row, ri) => (
                      <tr key={ri}>
                        <td className="row-num">{ri + 1}</td>
                        {active.columns.map((c) => (
                          <td key={c}>
                            <input
                              value={row[c] ?? ""}
                              onChange={(e) => updateCell(ri, c, e.target.value)}
                              data-testid={`cell-${ri}-${c}`}
                            />
                          </td>
                        ))}
                        <td style={{ textAlign: "center" }}>
                          <button onClick={() => deleteRow(ri)} className="text-stone-400 hover:text-red-700 p-1">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {active.rows.length === 0 && (
                      <tr><td colSpan={active.columns.length + 2} style={{ textAlign: "center", padding: "32px", color: "#a8a29e" }}>
                        Bảng trống — bấm "Thêm dòng" để bắt đầu
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 mx-auto rounded-sm bg-emerald-900 flex items-center justify-center mb-4">
                  <FileSpreadsheet className="w-8 h-8 text-amber-300" />
                </div>
                <h3 className="font-serif text-2xl font-bold mb-2">Quy trình Excel tích hợp</h3>
                <p className="text-sm text-stone-600 mb-6">
                  Upload file Excel → chỉnh sửa inline như Google Sheets → lưu trên hệ thống → xuất lại Excel
                  hoặc đẩy thẳng vào Master Data của ERP.
                </p>
                <Button onClick={() => fileRef.current?.click()} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm">
                  <Upload className="w-4 h-4 mr-1.5" />Upload file Excel đầu tiên
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {active && <ApplyMasterDialog open={applyOpen} setOpen={setApplyOpen} ws={active} />}
    </div>
  );
}

function ApplyMasterDialog({ open, setOpen, ws }) {
  const TARGETS = {
    employees: { label: "Nhân viên", fields: [["code","Mã NV"],["full_name","Họ tên"],["phone","SĐT"],["position","Chức vụ"],["base_salary","Lương cơ bản"]] },
    suppliers: { label: "Nhà cung cấp", fields: [["code","Mã"],["name","Tên NCC"],["phone","SĐT"]] },
    customers: { label: "Khách hàng", fields: [["code","Mã"],["name","Tên KH"],["phone","SĐT"]] },
    skus:      { label: "Sản phẩm",   fields: [["code","Mã SP"],["name","Tên SP"],["unit","Đơn vị"],["price","Giá bán"]] },
  };
  const [target, setTarget] = useState("employees");
  const [mapping, setMapping] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/excel/workspaces/${ws.id}/apply-to-master`, { target, mapping });
      setResult(r.data);
      toast.success(`Đã đẩy ${r.data.inserted} bản ghi vào ${TARGETS[target].label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    } finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-sm max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Đẩy dữ liệu vào Master Data</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Đẩy vào bảng nào?</Label>
            <Select value={target} onValueChange={(v) => { setTarget(v); setMapping({}); setResult(null); }}>
              <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TARGETS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Map cột Excel → trường ERP</Label>
            <div className="border border-stone-200 rounded-sm divide-y divide-stone-100 mt-1">
              {TARGETS[target].fields.map(([k, lbl]) => (
                <div key={k} className="px-3 py-2 flex items-center gap-2">
                  <div className="w-1/3 text-xs font-semibold">{lbl}</div>
                  <ChevronRight className="w-3 h-3 text-stone-400" />
                  <Select value={mapping[k] || undefined} onValueChange={(v) => setMapping((m) => ({ ...m, [k]: v }))}>
                    <SelectTrigger className="h-8 rounded-sm flex-1"><SelectValue placeholder="(bỏ qua)" /></SelectTrigger>
                    <SelectContent>{ws.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm">
              ✓ Đã thêm <strong>{result.inserted}</strong> bản ghi. {result.errors?.length > 0 && `${result.errors.length} lỗi.`}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Đóng</Button>
          <Button onClick={run} disabled={running} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-apply-run">
            {running ? "Đang đẩy…" : "Đẩy dữ liệu"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
